import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthenticated } from './hooks/useAuthenticated';
import '@fontsource/poppins';

// Pages
import Pricing from './pages/Pricing';
import Dashboard from './pages/Dashboard';
import CompletedRounds from './pages/CompletedRounds';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ResetPassword from './pages/ResetPassword';
import Admin from './pages/Admin';
import AdminUsers from './pages/AdminUsers';
import Quiz from './pages/Quiz';

// Components
import NavBar from './components/NavBar';
import AuthRoute from './components/AuthRoute';
import UnauthRoute from './components/UnauthRoute';

// Styles
import GlobalStyles from '@mui/material/GlobalStyles';
import QuestionBank from './pages/QuestionBank';
import CreateQuestion from './pages/CreateQuestion';
import { Bookmarks } from './pages/Bookmarks';
import Landing from './pages/Landing';
import Success from './pages/Success';
import Profile from './pages/Profile';
import EditQuestion from './pages/EditQuestion';

import FinalExam from './pages/FinalExam';
import MyExams from './pages/MyExams';
import Contact from './pages/Contact';
import FAQ from './pages/FAQ';

const globalStyles = (
  <GlobalStyles
    styles={{
      body: {
        background:
          'linear-gradient(to bottom,rgb(227, 248, 255),rgb(120, 162, 240))',
        margin: 0,
        padding: 0,
        fontFamily: '"Poppins", sans-serif',
        minHeight: '111.11vh',
      },
      '*': {
        boxSizing: 'border-box',
      },
    }}
  />
);

function App() {
  const { isAuthenticated, loading, isPlanActive } = useAuthenticated();
  const location = useLocation();

  const shouldShowNavBar = ![
    '/login',
    '/signup',
    '/reset-password',
    '/pricing',
    '/',
  ].includes(location.pathname);

  if (loading) {
    return null;
  }

  return (
    <>
      {globalStyles}
      {isAuthenticated && shouldShowNavBar && <NavBar />}
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<UnauthRoute element={<Login />} />} />
        <Route path="/signup" element={<UnauthRoute element={<Signup />} />} />
        <Route
          path="/reset-password"
          element={<UnauthRoute element={<ResetPassword />} />}
        />

        <Route path="/pricing" element={<AuthRoute element={<Pricing />} />} />
        <Route path="/success" element={<Success />} />
        <Route
          path="/dashboard"
          element={<AuthRoute element={<Dashboard />} requirePlan />}
        />
        <Route
          path="/completed-rounds"
          element={<AuthRoute element={<CompletedRounds />} requirePlan />}
        />
        <Route
          path="/my-exams"
          element={<AuthRoute element={<MyExams />} requirePlan />}
        />
        <Route
          path="/my-bookmarks"
          element={<AuthRoute element={<Bookmarks />} requirePlan />}
        />
        <Route path="/profile" element={<AuthRoute element={<Profile />} />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/faq" element={<FAQ />} />

        <Route
          path="/quiz/:id"
          element={<AuthRoute element={<Quiz />} requirePlan />}
        />

        <Route
          path="/final-exam/:id"
          element={<AuthRoute element={<FinalExam />} requirePlan />}
        />
        <Route
          path="/admin"
          element={<AuthRoute element={<Admin />} forAdmin />}
        />
        <Route
          path="/admin/users"
          element={<AuthRoute element={<AdminUsers />} forAdmin />}
        />
        <Route
          path="/admin/question-bank"
          element={<AuthRoute element={<QuestionBank />} forAdmin />}
        />
        <Route
          path="/admin/questions/new"
          element={<AuthRoute element={<CreateQuestion />} forAdmin />}
        />
        <Route
          path="/admin/questions/:id"
          element={<AuthRoute element={<EditQuestion />} forAdmin />}
        />
        <Route path="*" element={<Navigate to="/dashboard" />} />
      </Routes>
    </>
  );
}

export default App;
